import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, forkJoin, switchMap, timer } from 'rxjs';
import {
  NOT_ACCEPTED_STATES,
  SmartBroadcastService,
} from '../../../../../services/smart-broadcast/smart-broadcast.service';
import { formatCount } from '../../../../../../utils/sms';

type Phase = 'working' | 'done' | 'paused' | 'unknown';

const POLL_MS = 5000;
/** No new messages for this long: stop asking and say so. */
const STALL_MS = 2 * 60 * 1000;
/** Stop following after this long regardless. */
const MAX_MS = 45 * 60 * 1000;

/**
 * Step 5 — after the server's 202. It sends one row at a time and stores one
 * message per row under the broadcast's name, so progress is the count of
 * messages with that name (minus any that had it before), and "not accepted"
 * is the count whose gateway reply was a refusal. Receipts are not recorded
 * for these messages, so nothing here claims delivery.
 */
@Component({
  selector: 'sb-send-progress',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './send-progress.component.html',
  styleUrls: ['../../sb-shared.css', './send-progress.component.css'],
})
export class SendProgressComponent implements OnInit {
  /** Start a new broadcast from scratch. */
  @Output() restart = new EventEmitter<void>();

  store = inject(SmartBroadcastService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly submitted = signal<number | null>(null);
  readonly notAccepted = signal<number | null>(null);
  readonly phase = signal<Phase>('working');
  readonly formatCount = formatCount;

  private poll?: Subscription;
  private misses = 0;
  private last = -1;
  private lastChange = 0;
  private started = 0;

  readonly sent = computed(() => this.store.sent());
  readonly expected = computed(() => this.sent()?.expected ?? 0);

  readonly share = computed(() => {
    const expected = this.expected();
    const done = this.submitted() ?? 0;
    return expected ? Math.min(1, done / expected) : 0;
  });

  /** Accepted is only claimed once the refusals for this send are known. */
  readonly accepted = computed(() => {
    const done = this.submitted();
    const refused = this.notAccepted();
    if (done === null || refused === null) return null;
    return Math.max(0, done - refused);
  });

  readonly toGo = computed(() => Math.max(0, this.expected() - (this.submitted() ?? 0)));

  /**
   * What the status region says. It changes only when the phase changes or
   * progress passes a quarter, not on every poll.
   */
  readonly announcement = computed(() => {
    const expected = this.expected();
    const messages = `${formatCount(expected)} ${expected === 1 ? 'message' : 'messages'}`;
    switch (this.phase()) {
      case 'done':
        return `Sent. All ${messages} are with the network.`;
      case 'paused':
        return `No new messages for a couple of minutes. ${formatCount(this.submitted() ?? 0)} of ${messages} handed over so far.`;
      case 'unknown':
        return 'Sent to the server. Progress can’t be shown right now; the messages appear in Messages as they go out.';
      default: {
        if (this.submitted() === null) return '';
        const quarter = Math.floor(this.share() * 4);
        return quarter ? `${quarter * 25}% of ${messages} handed to the network.` : `Sending ${messages}.`;
      }
    }
  });

  ngOnInit() {
    this.started = Date.now();
    const sent = this.sent();
    if (!this.isBrowser || !sent) return;
    // Without a count of the messages that already had this name, the count
    // for it can't be read as progress.
    if (sent.baseTotal === null) {
      this.phase.set('unknown');
      return;
    }
    this.follow();
  }

  /** Ask for counts every few seconds until done, paused or unavailable. */
  follow() {
    const sent = this.sent();
    if (!sent) return;
    this.poll?.unsubscribe();
    this.phase.set('working');
    this.misses = 0;
    this.lastChange = Date.now();
    this.poll = timer(1500, POLL_MS)
      .pipe(
        switchMap(() =>
          forkJoin({
            total: this.store.countMessages(sent.subject),
            refused: this.store.countMessages(sent.subject, NOT_ACCEPTED_STATES),
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ total, refused }) => this.update(total, refused));
  }

  private update(total: number | null, refused: number | null) {
    const sent = this.sent();
    if (!sent) return this.stop('unknown');

    if (total === null) {
      this.misses++;
      if (this.misses >= 3) this.stop(this.submitted() === null ? 'unknown' : 'paused');
      return;
    }
    this.misses = 0;

    if (sent.baseTotal === null) return this.stop('unknown');
    const done = Math.min(sent.expected, Math.max(0, total - sent.baseTotal));
    this.submitted.set(done);
    if (refused !== null && sent.baseNotAccepted !== null) {
      this.notAccepted.set(Math.min(done, Math.max(0, refused - sent.baseNotAccepted)));
    }

    if (done !== this.last) {
      this.last = done;
      this.lastChange = Date.now();
    }
    if (done >= sent.expected) return this.stop('done');
    const now = Date.now();
    if (now - this.lastChange > STALL_MS || now - this.started > MAX_MS) this.stop('paused');
  }

  private stop(phase: Phase) {
    this.phase.set(phase);
    this.poll?.unsubscribe();
    this.poll = undefined;
  }

  checkAgain() {
    this.started = Date.now();
    this.follow();
  }
}
